import { createSlice } from '@reduxjs/toolkit';

// Initial state
const initialState = {
  // Navigation and layout
  showNavbar: false,
  isMenuOpen: false,
  isProfileMenuOpen: false,

  // Splash screen and age verification
  showSplash: true,
  showAgeVerification: false,
  hasShownSplash: false,

  // Loading states
  isLoading: false,
  loadingMessage: '',

  // Error handling
  error: null,
  errorType: null, // 'auth', 'network', 'validation', etc.

  // Notifications
  notifications: [],

  // Theme and appearance
  theme: 'dark',
  fontSize: 'medium',

  // Mobile responsiveness
  isMobile: false,
  screenSize: 'desktop', // 'mobile', 'tablet', 'desktop'

  // Performance monitoring
  performanceMetrics: {
    pageLoadTime: 0,
    renderTime: 0,
    memoryUsage: 0,
  },

  // Accessibility
  accessibility: {
    reducedMotion: false,
    highContrast: false,
    screenReader: false,
  },
};

// UI slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Navigation
    setShowNavbar: (state, action) => {
      state.showNavbar = action.payload;
    },
    toggleMenu: state => {
      state.isMenuOpen = !state.isMenuOpen;
    },
    closeMenu: state => {
      state.isMenuOpen = false;
    },
    toggleProfileMenu: state => {
      state.isProfileMenuOpen = !state.isProfileMenuOpen;
    },
    openProfileMenu: state => {
      state.isProfileMenuOpen = true;
    },
    closeProfileMenu: state => {
      state.isProfileMenuOpen = false;
    },

    // Splash screen and age verification
    setShowSplash: (state, action) => {
      state.showSplash = action.payload;
    },
    setShowAgeVerification: (state, action) => {
      state.showAgeVerification = action.payload;
    },
    setHasShownSplash: (state, action) => {
      state.hasShownSplash = action.payload;
    },

    // Loading states
    setLoading: (state, action) => {
      state.isLoading = action.payload.isLoading;
      state.loadingMessage = action.payload.message || '';
    },
    clearLoading: state => {
      state.isLoading = false;
      state.loadingMessage = '';
    },

    // Error handling
    setError: (state, action) => {
      state.error = action.payload.message;
      state.errorType = action.payload.type || 'general';
    },
    clearError: state => {
      state.error = null;
      state.errorType = null;
    },

    // Notifications
    addNotification: (state, action) => {
      const notification = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    clearAllNotifications: state => {
      state.notifications = [];
    },

    // Theme and appearance
    setTheme: (state, action) => {
      state.theme = action.payload;
    },
    setFontSize: (state, action) => {
      state.fontSize = action.payload;
    },

    // Mobile responsiveness
    setIsMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    setScreenSize: (state, action) => {
      state.screenSize = action.payload;
    },

    // Performance monitoring
    updatePerformanceMetrics: (state, action) => {
      state.performanceMetrics = {
        ...state.performanceMetrics,
        ...action.payload,
      };
    },

    // Accessibility
    updateAccessibility: (state, action) => {
      state.accessibility = {
        ...state.accessibility,
        ...action.payload,
      };
    },

    // Reset UI state
    resetUIState: state => {
      return {
        ...initialState,
        theme: state.theme, // Preserve theme
        fontSize: state.fontSize, // Preserve font size
        accessibility: state.accessibility, // Preserve accessibility settings
      };
    },
  },
});

// Export actions
export const {
  setShowNavbar,
  toggleMenu,
  closeMenu,
  toggleProfileMenu,
  openProfileMenu,
  closeProfileMenu,
  setShowSplash,
  setShowAgeVerification,
  setHasShownSplash,
  setLoading,
  clearLoading,
  setError,
  clearError,
  addNotification,
  removeNotification,
  clearAllNotifications,
  setTheme,
  setFontSize,
  setIsMobile,
  setScreenSize,
  updatePerformanceMetrics,
  updateAccessibility,
  resetUIState,
} = uiSlice.actions;

// Selectors
export const selectUI = state => state.ui;
export const selectShowNavbar = state => state.ui.showNavbar;
export const selectIsMenuOpen = state => state.ui.isMenuOpen;
export const selectIsProfileMenuOpen = state => state.ui.isProfileMenuOpen;
export const selectShowSplash = state => state.ui.showSplash;
export const selectShowAgeVerification = state => state.ui.showAgeVerification;
export const selectHasShownSplash = state => state.ui.hasShownSplash;
export const selectIsLoading = state => state.ui.isLoading;
export const selectLoadingMessage = state => state.ui.loadingMessage;
export const selectError = state => state.ui.error;
export const selectErrorType = state => state.ui.errorType;
export const selectNotifications = state => state.ui.notifications;
export const selectTheme = state => state.ui.theme;
export const selectFontSize = state => state.ui.fontSize;
export const selectIsMobile = state => state.ui.isMobile;
export const selectScreenSize = state => state.ui.screenSize;
export const selectPerformanceMetrics = state => state.ui.performanceMetrics;
export const selectAccessibility = state => state.ui.accessibility;

// Export reducer
export default uiSlice.reducer;
